import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Globe, Menu, X, ArrowRight, ArrowUpRight, MessageCircle, Mail,
  Smartphone, ShoppingCart, Boxes, TrendingUp, Users, CalendarCheck,
  Truck, LayoutDashboard, Building2, Sparkles, Check, Send,
  Instagram, Facebook, Linkedin, Twitter, Youtube, Music2,
  Paperclip, Loader2,
} from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api, imgUrl, thumbUrl, fmtErr } from "../lib/api";

const CATEGORY_ICONS = {
  "tiendas-en-linea": ShoppingCart,
  "aplicaciones-moviles": Smartphone,
  "sistemas-de-inventario": Boxes,
  "sistemas-de-ventas": TrendingUp,
  "gestion-de-clientes": Users,
  "reservas-y-citas": CalendarCheck,
  "aplicaciones-de-entregas": Truck,
  "paneles-de-control": LayoutDashboard,
  "plataformas-empresariales": Building2,
  "soluciones-personalizadas": Sparkles,
};

const SOCIAL_ICONS = {
  instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
  x: Twitter, tiktok: Music2, youtube: Youtube,
};

function Img({ src, alt, className, ...rest }) {
  const [err, setErr] = useState(false);
  if (err || !src) {
    return (
      <div className={`${className} bg-gradient-to-br from-[#0B1021] to-[#030305] flex items-center justify-center`} role="img" aria-label={alt}>
        <Sparkles className="w-8 h-8 text-[#0A84FF]/50" strokeWidth={1.5} />
      </div>
    );
  }
  // SIN loading="lazy": en iOS Safari las imágenes lazy dentro de secciones
  // below-fold / scrollers pueden no cargar tras scroll rápido (contenido
  // que "aparece a medias"). Las tarjetas usan miniaturas de ~6-15 KB.
  return <img src={src} alt={alt} className={className} decoding="async" onError={() => { console.warn("Error cargando imagen:", src); setErr(true); }} {...rest} />;
}

// Reveal al hacer scroll SIN bloquear el contenido: visible por defecto.
// useLayoutEffect "arma" la animación antes del primer pintado (sin flash);
// un IntersectionObserver la dispara al entrar en viewport y una red de
// seguridad la fuerza a los 4 s si algo falla. La transición es CSS puro.
// Redes de seguridad dobles: WebKit puede pausar timers/rAF con la pestaña
// oculta o ahorro de energía; si la animación no corrió, forzamos visibilidad
// con una media query + comprobación por rAF. El contenido NUNCA puede
// quedarse invisible de forma permanente.
function Reveal({ children, className = "", delay = 0, ...rest }) {
  const ref = useRef(null);
  const [cls, setCls] = useState("");
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setCls("reveal-armed reveal-in"); return; }
    setCls("reveal-armed");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { setCls("reveal-armed reveal-in"); io.disconnect(); }
      });
    }, { rootMargin: "0px 0px -60px 0px", threshold: 0.05 });
    io.observe(el);
    const t = setTimeout(() => setCls("reveal-armed reveal-in"), 2500);
    // rAF de respaldo: si a los 2.5 s sigue armada sin entrar, la liberamos
    let raf;
    const t2 = setTimeout(() => {
      raf = requestAnimationFrame(() => setCls((c) => c.includes("reveal-in") ? c : "reveal-armed reveal-in"));
    }, 2600);
    return () => { io.disconnect(); clearTimeout(t); clearTimeout(t2); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return (
    <div ref={ref} className={`${cls} ${className}`.trim()}
      style={delay ? { "--reveal-delay": `${delay}s` } : undefined} {...rest}>
      {children}
    </div>
  );
}

function MaskedTitle({ text, className }) {
  // Animación 100% CSS (compositor GPU): el texto se pinta en el primer render
  // y nunca depende de JavaScript para aparecer.
  const words = (text || "").split(" ").filter(Boolean);
  return (
    <h1 className={className} style={{ whiteSpace: "pre-wrap" }} data-testid="hero-title">
      {words.map((w, i) => (
        <span key={i}>
          <span className="inline-block overflow-hidden align-bottom pb-1 -mb-1">
            <span className="hero-word" style={{ animationDelay: `${0.05 + i * 0.045}s` }}>
              {w}
            </span>
          </span>
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </h1>
  );
}

function Header({ onNav }) {
  const { site, lang, setLang, t } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const logo = site?.brand?.logo_url;
  const h = site?.brand?.header_logo_height || 48;
  const links = [
    ["solutions", "nav.solutions"],
    ["services", "nav.services"],
    ["process", "nav.process"],
    ["contact", "nav.contact"],
  ];

  const go = (id) => { setOpen(false); onNav(id); };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "backdrop-blur-2xl bg-[#030305]/80 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between h-20">
        <button onClick={() => go("top")} className="flex items-center gap-3" data-testid="header-logo">
          {logo && (
            <img src={imgUrl(logo)} alt="TERAVIX" style={{ height: h }} className="w-auto drop-shadow-[0_0_18px_rgba(10,132,255,0.35)]" />
          )}
          <span className="font-display font-bold tracking-[0.25em] text-xs sm:text-sm text-chrome">TERAVIX</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {links.map(([id, key]) => (
            <button key={id} onClick={() => go(id)} data-testid={`nav-${id}`}
              className="text-sm text-slate-300 hover:text-white transition-colors duration-300 font-medium">
              {t(key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-md p-1" data-testid="lang-switcher">
            {["es", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                  lang === l ? "bg-[#0A84FF] text-white" : "text-slate-400 hover:text-white"
                }`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => go("contact")} data-testid="header-quote-btn"
            className="hidden lg:inline-flex btn-primary-glow !px-5 !py-2.5 text-xs">
            {t("nav.quote")}
          </button>
          <button className="lg:hidden p-2 text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menú">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="lg:hidden overflow-hidden backdrop-blur-2xl bg-[#030305]/95 border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map(([id, key]) => (
                <button key={id} onClick={() => go(id)} data-testid={`mnav-${id}`}
                  className="text-left text-lg font-display font-medium text-slate-200 py-2">
                  {t(key)}
                </button>
              ))}
              <button onClick={() => go("contact")} data-testid="mnav-quote" className="btn-primary-glow mt-2">
                {t("nav.quote")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero({ onNav, onQuote }) {
  const { site, lang, L, t } = useSite();
  const hero = site?.hero || {};
  const ref = useRef(null);
  // Parallax/scroll-fade solo en escritorio: en táctil el scroll nativo de iOS
  // no sincroniza bien con rAF y genera tirones.
  const isTouch = window.matchMedia("(pointer: coarse)").matches;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const banner = hero.banner_desktop || "";
  const bannerMobile = hero.banner_mobile || banner;
  const title = L(hero, "title");
  const logo = site?.brand?.logo_url;
  const align = hero.content_align || "left";
  const centered = align === "center";

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] flex overflow-hidden" data-testid="hero-section">
      <motion.div style={isTouch ? undefined : { y: bgY }} className="absolute inset-0">
        {banner ? (
          <picture>
            <source media="(max-width: 767px)" srcSet={imgUrl(bannerMobile)} />
            <img src={imgUrl(banner)} alt="" fetchpriority="high" className="w-full h-full object-cover"
              style={{ objectPosition: hero.image_position || "center" }} data-testid="hero-banner" />
          </picture>
        ) : (
          <div className="w-full h-full bg-[#030305]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#030305]/80 via-[#030305]/55 to-[#030305]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030305]/70 via-transparent to-[#030305]/40" />
      </motion.div>

      <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[38rem] h-[38rem] rounded-full bg-[#0A84FF]/20 blur-[140px] animate-pulse-glow pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[#00C2FF]/10 blur-[120px] pointer-events-none" />

      {logo && !banner && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: "easeOut", delay: 0.5 }}
          className="absolute right-[-2rem] top-1/2 -translate-y-1/2 hidden lg:block pointer-events-none"
        >
          <div className="relative">
            <div className="absolute inset-[-3rem] rounded-full border border-[#00C2FF]/20 animate-spin-orbit" />
            <div className="absolute inset-[-6rem] rounded-full border border-white/5" />
            <img src={imgUrl(logo)} alt="" className="w-[23rem] h-[23rem] rounded-full object-cover opacity-90 drop-shadow-[0_0_80px_rgba(10,132,255,0.45)] animate-float-slow" />
          </div>
        </motion.div>
      )}

      <motion.div style={isTouch ? undefined : { opacity: fade }} className={`relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full my-auto pt-28 pb-16 flex flex-col ${centered ? "items-center text-center" : "items-start text-left"}`}>
        <p
          className={`eyebrow hero-fade mb-6 flex items-center gap-3 ${centered ? "justify-center" : ""}`}
          style={{ animationDelay: "0.1s" }} data-testid="hero-slogan"
        >
          <span className="inline-block w-10 h-px bg-[#00C2FF]" />
          {L(hero, "slogan")}
        </p>

        <MaskedTitle
          text={title}
          className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl text-chrome"
        />

        <p
          className="hero-fade mt-7 max-w-xl text-base md:text-lg text-slate-300 font-light leading-relaxed"
          style={{ animationDelay: "0.35s" }}
          data-testid="hero-subtitle"
        >
          {L(hero, "subtitle")}
        </p>

        {L(hero, "description") && (
          <p
            className={`hero-fade mt-4 max-w-lg text-sm text-slate-400 font-light leading-relaxed ${centered ? "mx-auto" : ""}`}
            style={{ animationDelay: "0.45s" }}
            data-testid="hero-description"
          >
            {L(hero, "description")}
          </p>
        )}

        <div
          className={`hero-fade mt-10 flex flex-col sm:flex-row gap-4 ${centered ? "justify-center items-center" : ""}`}
          style={{ animationDelay: "0.55s" }}
        >
          <button onClick={() => onNav("solutions")} className="btn-primary-glow" data-testid="hero-explore-btn">
            {L(hero, "btn_primary")} <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={onQuote} className="btn-ghost-glass" data-testid="hero-quote-btn">
            {L(hero, "btn_secondary")} <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>

      <div
        className="hero-fade absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500"
        style={{ animationDelay: "1.2s" }}
      >
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#00C2FF]/60 to-transparent" />
      </div>
    </section>
  );
}

function Marquee({ categories }) {
  const { L } = useSite();
  const items = categories.map((c) => L(c, "name"));
  const row = [...items, ...items];
  return (
    <div className="relative py-5 border-y border-white/5 bg-white/[0.015] overflow-hidden" data-testid="marquee">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {row.map((name, i) => (
          <span key={i} className="mx-8 font-display text-sm md:text-base font-medium uppercase tracking-[0.3em] text-slate-500 flex items-center gap-8">
            {name}
            <span className="w-1.5 h-1.5 rounded-full bg-[#0A84FF]/70" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Lightbox({ project, categories, onClose, onQuote }) {
  const { t, L, lang } = useSite();
  const [idx, setIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const images = [project.main_image, ...(project.gallery || [])].filter(Boolean);
  const catName = (slug) => {
    const c = categories.find((x) => x.slug === slug);
    return c ? L(c, "name") : slug;
  };
  const features = lang === "es" ? project.features_es : project.features_en;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="project-lightbox"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="glass-heavy rounded-3xl w-full max-w-2xl max-h-[88vh] overflow-y-auto scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        {images.length > 0 && (
          <div className="relative">
            <div className={`${zoomed ? "max-h-[70vh] overflow-auto" : ""} bg-[#030305]`}>
              <Img src={imgUrl(images[idx])} alt={L(project, "name")} loading="eager" data-testid="lightbox-image"
                onClick={() => setZoomed(!zoomed)}
                className={zoomed ? "w-auto max-w-none min-w-full cursor-zoom-out" : "w-full aspect-[16/10] object-cover cursor-zoom-in"} />
            </div>
            {images.length > 1 && (
              <>
                <button onClick={() => setIdx((idx - 1 + images.length) % images.length)} data-testid="lightbox-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors">
                  ‹
                </button>
                <button onClick={() => setIdx((idx + 1) % images.length)} data-testid="lightbox-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors">
                  ›
                </button>
              </>
            )}
            <button onClick={onClose} data-testid="lightbox-close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {images.length > 1 && (
          <div className="flex gap-2 px-6 pt-4 overflow-x-auto scrollbar-hide">
            {images.map((im, i) => (
              <button key={i} onClick={() => { setIdx(i); setZoomed(false); }} data-testid={`lightbox-thumb-${i}`}
                className={`shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-colors ${i === idx ? "border-[#00C2FF]" : "border-white/10"}`}>
                <img src={imgUrl(thumbUrl(im))} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        )}
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A84FF]/20 border border-[#00C2FF]/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#00C2FF]">
              {catName(project.category)}
            </span>
            {project.is_demo && (
              <span className="rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-300">
                {t("projects.demo_badge")}
              </span>
            )}
          </div>
          <h3 className="font-display font-bold text-2xl text-white">{L(project, "name")}</h3>
          <p className="mt-3 text-sm text-slate-300/90 font-light leading-relaxed">{L(project, "description")}</p>
          {features?.length > 0 && (
            <ul className="mt-5 space-y-2">
              {features.map((f, j) => (
                <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check className="w-4 h-4 text-[#00C2FF] shrink-0 mt-0.5" strokeWidth={1.5} /> {f}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={() => onQuote(project)} className="btn-primary-glow !px-5 !py-2.5 !text-xs" data-testid="lightbox-quote">
              {t("projects.similar")}
            </button>
            {project.demo_url && (
              <a href={project.demo_url} target="_blank" rel="noopener noreferrer" className="btn-ghost-glass !px-5 !py-2.5 !text-xs" data-testid="lightbox-demo">
                {t("projects.visit")} <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ProjectCard({ p, categories, onOpen, onQuote, featured }) {
  const { t, L } = useSite();
  const Icon = CATEGORY_ICONS[p.category] || Sparkles;
  const catName = (slug) => {
    const c = categories.find((x) => x.slug === slug);
    return c ? L(c, "name") : slug;
  };
  return (
    <article
      className={`glass-heavy rounded-3xl overflow-hidden flex flex-col h-full hover:-translate-y-1 hover:border-[#00C2FF]/25 transition-[transform,border-color] duration-300 ${featured ? "md:col-span-2" : ""}`}
      data-testid={`project-card-${p.id}`}
    >
      <div className="flex items-center gap-2 px-5 pt-5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A84FF]/20 border border-[#00C2FF]/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#00C2FF]">
          <Icon className="w-3 h-3" strokeWidth={1.5} /> {catName(p.category)}
        </span>
        {p.is_demo && (
          <span className="rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-300"
            title={t("projects.demo_note")}>
            {t("projects.demo_badge")}
          </span>
        )}
      </div>
      {p.main_image && (
        <button onClick={() => onOpen(p)} className="px-5 pt-4 text-left" data-testid={`project-open-${p.id}`}>
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02]">
            <Img src={imgUrl(thumbUrl(p.main_image))} alt={L(p, "name")} decoding="async"
              className={`w-full object-cover ${featured ? "aspect-[16/8]" : "aspect-[4/3]"}`} />
          </div>
        </button>
      )}
      <div className="p-5 pt-4 flex flex-col flex-1">
        <h3 className="font-display font-bold text-lg text-white">{L(p, "name")}</h3>
        <p className="mt-2 text-sm text-slate-400 font-light leading-relaxed line-clamp-2">{L(p, "description")}</p>
        <div className="mt-auto pt-4 flex flex-wrap gap-2.5">
          <button onClick={() => onOpen(p)} className="btn-ghost-glass !px-4 !py-2 !text-xs" data-testid={`project-gallery-${p.id}`}>
            {t("projects.gallery")}
          </button>
          <button onClick={() => onQuote(p)} className="btn-primary-glow !px-4 !py-2 !text-xs" data-testid={`project-quote-${p.id}`}>
            {t("projects.similar")}
          </button>
        </div>
      </div>
    </article>
  );
}

function Projects({ projects, categories, onQuoteFor }) {
  const { t, L, lang } = useSite();
  const { site } = useSite();
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const filtered = filter === "all" ? projects : projects.filter((p) => p.category === filter);

  const groupsAll = categories
    .map((c) => ({ ...c, items: filtered.filter((p) => p.category === c.slug) }))
    .filter((g) => g.items.length > 0);
  // Móvil: prioriza 2 filas; usa la 3ª solo si hay exactamente 3 categorías con proyectos.
  const maxRows = groupsAll.length <= 3 ? groupsAll.length : 2;
  const groups = filter === "all" ? groupsAll.slice(0, maxRows) : groupsAll;

  return (
    <section id="solutions" className="relative py-16 md:py-24" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-4">{site?.sections?.[`projects_eyebrow_${lang}`]}</p>
          <h2 className="font-display font-bold tracking-tight text-base md:text-lg text-[#00C2FF] uppercase tracking-[0.2em] mb-3">
            {site?.sections?.[`projects_title_${lang}`]}
          </h2>
          <p className="font-display font-bold text-4xl sm:text-5xl text-chrome leading-tight">
            {lang === "es" ? "Construido para escalar." : "Built to scale."}
          </p>
        </Reveal>

        {projects.length > 0 && (
          <Reveal className="mt-10 flex flex-nowrap md:flex-wrap gap-2 overflow-x-auto scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0 pb-1" data-testid="project-filters">
            <button onClick={() => setFilter("all")} data-testid="filter-all"
              className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-300 border ${
                filter === "all" ? "bg-[#0A84FF] border-[#0A84FF] text-white" : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
              }`}>
              {t("projects.all")}
            </button>
            {categories.map((c) => (
              <button key={c.slug} onClick={() => setFilter(c.slug)} data-testid={`filter-${c.slug}`}
                className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-300 border ${
                  filter === c.slug ? "bg-[#0A84FF] border-[#0A84FF] text-white" : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
                }`}>
                {L(c, "name")}
              </button>
            ))}
          </Reveal>
        )}

        {filtered.length === 0 ? (
          <Reveal className="mt-14 glass-light rounded-3xl p-12 text-center" data-testid="projects-empty">
            <Sparkles className="w-8 h-8 mx-auto text-[#00C2FF] mb-4" strokeWidth={1.5} />
            <h3 className="font-display font-semibold text-xl text-white">{t("projects.empty_title")}</h3>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">{t("projects.empty_text")}</p>
          </Reveal>
        ) : (
          <>
            {/* Móvil: carruseles horizontales por categoría */}
            <div className="md:hidden mt-12 space-y-10" data-testid="projects-carousels">
              {groups.map((g) => {
                const Icon = CATEGORY_ICONS[g.slug] || Sparkles;
                return (
                  <div key={g.slug}>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-8 h-8 rounded-lg bg-[#0A84FF]/15 border border-[#00C2FF]/25 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#00C2FF]" strokeWidth={1.5} />
                      </span>
                      <h3 className="font-display font-semibold text-base text-white">{L(g, "name")}</h3>
                    </div>
                    <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-6 px-6 pb-1" data-testid={`carousel-${g.slug}`}>
                      {g.items.map((p) => (
                        <div key={p.id} className="min-w-[68%] snap-start">
                          <ProjectCard p={p} categories={categories} onOpen={setLightbox} onQuote={onQuoteFor} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Escritorio: bento grid */}
            <div className="hidden md:grid mt-14 grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}
                  className={i % 5 === 0 ? "col-span-2" : ""}>
                  <ProjectCard p={p} categories={categories} onOpen={setLightbox} onQuote={onQuoteFor} featured={i % 5 === 0} />
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <Lightbox project={lightbox} categories={categories} onClose={() => setLightbox(null)}
            onQuote={(p) => { setLightbox(null); onQuoteFor(p); }} />
        )}
      </AnimatePresence>
    </section>
  );
}

function ServiceCard({ s, onQuote }) {
  const { t, L, lang } = useSite();
  return (
    <div className={`relative rounded-3xl p-8 flex flex-col h-full ${s.is_promo ? "glass-glow" : "glass-heavy"} hover:-translate-y-1 transition-transform duration-300`}
      data-testid={`service-card-${s.id}`}>
      {s.image && (
        <div className="-mx-8 -mt-8 mb-6 h-44 overflow-hidden rounded-t-3xl border-b border-white/10">
          <Img src={imgUrl(thumbUrl(s.image))} alt={L(s, "title")} className="w-full h-full object-cover" decoding="async" />
        </div>
      )}
      {s.is_promo && (
        <span className="absolute -top-3 right-6 rounded-full bg-[#0A84FF] px-3 py-1 text-[10px] font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(10,132,255,0.5)]">
          {t("services.promo")}
        </span>
      )}
      <h3 className="font-display font-bold text-xl text-white">{L(s, "title")}</h3>
      {s.promo_on ? (
        <div className="mt-3 flex items-end flex-wrap gap-x-3 gap-y-2" data-testid={`service-promo-${s.id}`}>
          <span className="text-lg text-slate-500 line-through font-light" data-testid={`service-original-${s.id}`}>
            ${Number(s.price_original).toLocaleString("en-US")}
          </span>
          <span className="font-display text-3xl font-bold text-blue-glow" data-testid={`service-promo-price-${s.id}`}>
            ${Number(s.price_promo).toLocaleString("en-US")}
          </span>
          <span className="rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider" data-testid={`service-discount-${s.id}`}>
            {s.discount_pct}% {lang === "es" ? "de descuento" : "off"}
          </span>
          {L(s, "promo_label") && (
            <span className="w-full text-[11px] font-semibold text-amber-300" data-testid={`service-promo-label-${s.id}`}>{L(s, "promo_label")}</span>
          )}
        </div>
      ) : s.price_original > 0 ? (
        <p className="mt-3 font-display text-3xl font-bold text-blue-glow" data-testid={`service-price-${s.id}`}>
          ${Number(s.price_original).toLocaleString("en-US")}
        </p>
      ) : L(s, "price") && (
        <p className="mt-3 font-display text-3xl font-bold text-blue-glow">{L(s, "price")}</p>
      )}
      <p className="mt-3 text-sm text-slate-300/90 font-light leading-relaxed flex-1">{L(s, "description")}</p>
      {(lang === "es" ? s.features_es : s.features_en)?.length > 0 && (
        <ul className="mt-5 space-y-2">
          {(lang === "es" ? s.features_es : s.features_en).map((f, j) => (
            <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
              <Check className="w-4 h-4 text-[#00C2FF] shrink-0 mt-0.5" strokeWidth={1.5} /> {f}
            </li>
          ))}
        </ul>
      )}
      <button onClick={() => onQuote(s)} className="btn-ghost-glass mt-7 w-full !text-xs" data-testid={`service-quote-${s.id}`}>
        {t("services.cta")}
      </button>
    </div>
  );
}

function Services({ services, onQuoteFor }) {
  const { t, L, lang, site } = useSite();
  const deskRef = useRef(null);
  const scrollDesk = (dir) => deskRef.current?.scrollBy({ left: dir * 420, behavior: "smooth" });
  return (
    <section id="services" className="relative py-16 md:py-24 border-t border-white/5" data-testid="services-section">
      <div className="absolute top-1/3 left-0 w-[26rem] h-[26rem] rounded-full bg-[#0047FF]/10 blur-[130px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-4">{site?.sections?.[`services_eyebrow_${lang}`]}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-chrome leading-tight">
            {site?.sections?.[`services_title_${lang}`]}
          </h2>
        </Reveal>

        {services.length === 0 ? (
          <Reveal className="mt-14 glass-light rounded-3xl p-12 text-center" data-testid="services-empty">
            <Boxes className="w-8 h-8 mx-auto text-[#00C2FF] mb-4" strokeWidth={1.5} />
            <h3 className="font-display font-semibold text-xl text-white">{t("services.empty_title")}</h3>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">{t("services.empty_text")}</p>
          </Reveal>
        ) : (
          <>
            {/* Móvil: carrusel horizontal */}
            <div className="md:hidden mt-10 flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-6 px-6 pb-1" data-testid="services-carousel">
              {services.map((s) => (
                <div key={s.id} className="min-w-[80%] snap-start">
                  <ServiceCard s={s} onQuote={onQuoteFor} />
                </div>
              ))}
            </div>

            {/* Escritorio: cuadrícula hasta 3; carrusel con flechas si hay más */}
            {services.length <= 3 ? (
              <div className="hidden md:grid mt-14 grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((s, i) => (
                  <Reveal key={s.id} delay={(i % 3) * 0.08}>
                    <ServiceCard s={s} onQuote={onQuoteFor} />
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="hidden md:block mt-14 relative">
                <div ref={deskRef} className="flex gap-6 overflow-x-auto snap-x scrollbar-hide pb-1" data-testid="services-carousel-desktop">
                  {services.map((s) => (
                    <div key={s.id} className="min-w-[380px] snap-start">
                      <ServiceCard s={s} onQuote={onQuoteFor} />
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-6 justify-end">
                  <button onClick={() => scrollDesk(-1)} data-testid="services-prev"
                    className="w-10 h-10 rounded-full border border-white/20 bg-white/[0.04] flex items-center justify-center text-white hover:border-[#00C2FF]/50 transition-colors" aria-label="Anterior">‹</button>
                  <button onClick={() => scrollDesk(1)} data-testid="services-next"
                    className="w-10 h-10 rounded-full border border-white/20 bg-white/[0.04] flex items-center justify-center text-white hover:border-[#00C2FF]/50 transition-colors" aria-label="Siguiente">›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Process() {
  const { t, lang, site } = useSite();
  const steps = t("process.steps") || [];
  return (
    <section id="process" className="relative py-16 md:py-24 border-t border-white/5" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-4">{site?.sections?.[`process_eyebrow_${lang}`]}</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-chrome leading-tight">
            {site?.sections?.[`process_title_${lang}`]}
          </h2>
        </Reveal>
        <Reveal delay={0.1}
          className="mt-10 glass-heavy rounded-3xl p-6 md:p-10 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#0A84FF]/10 blur-[90px] pointer-events-none" />
          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
            {steps.map((s, i) => (
              <div key={i} className="group" data-testid={`process-step-${i + 1}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-9 h-9 shrink-0 rounded-xl bg-[#0A84FF]/15 border border-[#00C2FF]/25 flex items-center justify-center font-display font-bold text-[#00C2FF] text-sm">
                    {i + 1}
                  </span>
                  <span className="hidden lg:block flex-1 h-px bg-gradient-to-r from-[#00C2FF]/30 to-transparent" />
                </div>
                <h3 className="font-display font-semibold text-sm md:text-base text-white leading-snug">{s.title}</h3>
                <p className="mt-2 text-xs md:text-sm text-slate-400 font-light leading-relaxed line-clamp-3">{s.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Contact({ categories, prefillType }) {
  const { t, L, lang, site } = useSite();
  const [form, setForm] = useState({ name: "", business: "", contact: "", project_type: "", description: "", budget: "" });
  const [atts, setAtts] = useState([]);
  const fileRef = useRef(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (prefillType) setForm((f) => ({ ...f, project_type: prefillType }));
  }, [prefillType]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (atts.length + files.length > 6) {
      setError(t("form.attach_limit"));
      return;
    }
    setError("");
    for (const file of files) {
      const id = Math.random().toString(36).slice(2);
      const preview = URL.createObjectURL(file);
      setAtts((prev) => [...prev, { id, preview, url: "", status: "uploading", progress: 0, name: file.name }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/quotes/upload", fd, {
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 50;
            setAtts((prev) => prev.map((a) => (a.id === id ? { ...a, progress: pct } : a)));
          },
        });
        setAtts((prev) => prev.map((a) => (a.id === id ? { ...a, url: data.url, status: "done", progress: 100 } : a)));
      } catch (err) {
        setAtts((prev) => prev.map((a) => (a.id === id ? { ...a, status: "error" } : a)));
        setError(fmtErr(err) || t("form.attach_error"));
      }
    }
  };

  const removeAtt = (id) => setAtts((prev) => prev.filter((a) => a.id !== id));
  const uploading = atts.some((a) => a.status === "uploading");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.contact.trim() || !form.description.trim()) {
      setError(t("form.required"));
      return;
    }
    setSending(true);
    try {
      await api.post("/quotes", {
        ...form,
        lang,
        attachments: atts.filter((a) => a.status === "done").map((a) => a.url),
      });
      setSent(true);
    } catch (err) {
      setError(fmtErr(err) || t("form.error"));
    } finally {
      setSending(false);
    }
  };

  const wa = site?.contact?.whatsapp?.replace(/[^0-9]/g, "");
  const email = site?.contact?.email;

  return (
    <section id="contact" className="relative py-16 md:py-24 border-t border-white/5" data-testid="contact-section">
      <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-[#0A84FF]/10 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-10">
        <Reveal>
          <p className="eyebrow mb-4">{site?.sections?.[`contact_eyebrow_${lang}`]}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-chrome leading-tight">
            {site?.sections?.[`contact_title_${lang}`]}
          </h2>
          <p className="mt-5 text-slate-400 font-light max-w-md">{L(site?.contact, "business_info")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            {wa && (
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="btn-ghost-glass" data-testid="whatsapp-btn">
                <MessageCircle className="w-4 h-4 text-[#00C2FF]" strokeWidth={1.5} /> {t("contact.whatsapp")}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className="btn-ghost-glass" data-testid="email-btn">
                <Mail className="w-4 h-4 text-[#00C2FF]" strokeWidth={1.5} /> {t("contact.email")}
              </a>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="glass-heavy rounded-3xl p-8 md:p-10">
          {sent ? (
            <div className="text-center py-14" data-testid="quote-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#0A84FF]/15 border border-[#00C2FF]/40 flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(10,132,255,0.3)]">
                <Check className="w-8 h-8 text-[#00C2FF]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display font-bold text-2xl text-white">{t("form.success_title")}</h3>
              <p className="mt-3 text-sm text-slate-400 max-w-sm mx-auto">{t("form.success_text")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("form.name")} *</label>
                  <input className="input-glass" value={form.name} onChange={set("name")} placeholder={t("form.name_ph")} data-testid="quote-name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("form.business")}</label>
                  <input className="input-glass" value={form.business} onChange={set("business")} placeholder={t("form.business_ph")} data-testid="quote-business" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("form.contact")} *</label>
                <input className="input-glass" value={form.contact} onChange={set("contact")} placeholder={t("form.contact_ph")} data-testid="quote-contact" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("form.type")}</label>
                  <select className="input-glass appearance-none" value={form.project_type} onChange={set("project_type")} data-testid="quote-type">
                    <option value="" className="bg-[#0B1021]">{t("form.type_ph")}</option>
                    {categories.map((c) => (
                      <option key={c.slug} value={L(c, "name")} className="bg-[#0B1021]">{L(c, "name")}</option>
                    ))}
                    <option value={t("form.other")} className="bg-[#0B1021]">{t("form.other")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("form.budget")}</label>
                  <input className="input-glass" value={form.budget} onChange={set("budget")} placeholder={t("form.budget_ph")} data-testid="quote-budget" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("form.description")} *</label>
                <textarea rows={4} className="input-glass resize-none" value={form.description} onChange={set("description")} placeholder={t("form.description_ph")} data-testid="quote-description" />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("form.attach_label")}</label>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden"
                  onChange={addFiles} data-testid="quote-attachments-input" />
                <div className="grid grid-cols-3 gap-2.5">
                  {atts.map((a) => (
                    <div key={a.id} className="relative rounded-xl overflow-hidden border border-white/10 aspect-square group" data-testid={`quote-att-${a.id}`}>
                      <img src={a.preview} alt="" className={`w-full h-full object-cover ${a.status === "error" ? "opacity-30" : ""}`} />
                      {a.status === "uploading" && (
                        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-1.5">
                          <Loader2 className="w-4 h-4 animate-spin text-[#00C2FF]" />
                          <span className="text-[10px] text-slate-300">{a.progress}%</span>
                        </div>
                      )}
                      {a.status === "error" && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[10px] text-red-400 font-semibold">{t("form.attach_error")}</span>
                        </div>
                      )}
                      <button type="button" onClick={() => removeAtt(a.id)} data-testid={`quote-att-remove-${a.id}`}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {atts.length < 6 && (
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="quote-attach-btn"
                      className="rounded-xl border border-dashed border-white/20 aspect-square flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:border-[#00C2FF]/50 hover:text-[#00C2FF] transition-colors bg-white/[0.02]">
                      <Paperclip className="w-5 h-5" strokeWidth={1.5} />
                      <span className="text-[10px] leading-tight text-center px-2">{t("form.attach_add")}</span>
                    </button>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-slate-500">{t("form.attach_hint")}</p>
              </div>

              {error && <p className="text-sm text-red-400" data-testid="quote-error">{error}</p>}
              <button type="submit" disabled={sending || uploading} className="btn-primary-glow w-full disabled:opacity-60" data-testid="quote-submit">
                {sending ? t("form.sending") : uploading ? t("form.attach_uploading") : t("form.send")} <Send className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const { site, lang, L, t } = useSite();
  const logo = site?.brand?.logo_url;
  const socials = Object.entries(site?.social || {}).filter(([, v]) => v);
  return (
    <footer className="relative border-t border-white/10 py-14" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex items-center gap-4">
          {logo && <img src={imgUrl(logo)} alt="TERAVIX" className="h-12 w-auto" />}
          <div>
            <p className="font-display font-bold tracking-[0.25em] text-sm text-chrome">TERAVIX</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#00C2FF] mt-1">{L(site?.hero, "slogan")}</p>
          </div>
        </div>
        <p className="text-xs text-slate-500 text-center max-w-sm font-light">
          {L(site?.sections, "footer_text")}
        </p>
        <div className="flex items-center gap-3">
          {socials.map(([k, v]) => {
            const Icon = SOCIAL_ICONS[k];
            return Icon ? (
              <a key={k} href={v} target="_blank" rel="noopener noreferrer" data-testid={`social-${k}`}
                className="w-9 h-9 rounded-full border border-white/15 bg-white/[0.04] flex items-center justify-center text-slate-400 hover:text-[#00C2FF] hover:border-[#00C2FF]/50 transition-colors duration-300">
                <Icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ) : null;
          })}
        </div>
      </div>
      <p className="mt-10 text-center text-[11px] text-slate-600">
        © {new Date().getFullYear()} TERAVIX. {t("footer.rights")}
      </p>
    </footer>
  );
}

export default function Landing() {
  const { site } = useSite();
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [prefillType, setPrefillType] = useState("");
  const lenisRef = useRef(null);

  useEffect(() => {
    // Lenis solo en escritorio: en táctil el scroll nativo de iOS/Android es más fiable
    if (window.matchMedia("(pointer: coarse)").matches) return;
    let lenis;
    let raf;
    let cancelled = false;
    import("lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      lenis = new Lenis({ lerp: 0.09 });
      lenisRef.current = lenis;
      const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    });
    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); if (lenis) lenis.destroy(); };
  }, []);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  // Service worker (2ª visita instantánea) + modo de pintado para iPhone.
  // iOS Safari se queda sin memoria de capas GPU con backdrop-filter + blurs
  // + ruido y pinta NEGRO: en táctil el modo sólido es el DEFECTO (se puede
  // forzar el modo completo con ?lite=0 o volver al sólido con ?lite=1).
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("lite") === "1") localStorage.setItem("tvx_paint_lite", "1");
      else if (q.get("lite") === "0") localStorage.setItem("tvx_paint_lite", "0");
      const pref = localStorage.getItem("tvx_paint_lite");
      const touch = window.matchMedia("(pointer: coarse)").matches;
      const lite = pref !== null ? pref === "1" : touch;
      if (lite) document.documentElement.classList.add("paint-lite");
    } catch (_) {}
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -70, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const quoteFor = (item) => {
    if (item.category) {
      const cat = categories.find((c) => c.slug === item.category);
      if (cat) setPrefillType(document.documentElement.lang === "en" ? cat.name_en : cat.name_es);
    }
    scrollTo("contact");
  };

  if (!site) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center" data-testid="site-loading">
        <div className="w-12 h-12 rounded-full border-2 border-[#0A84FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#030305] text-white min-h-screen" data-testid="landing-page">
      <Header onNav={scrollTo} />
      <Hero onNav={scrollTo} onQuote={() => scrollTo("contact")} />
      <Marquee categories={categories} />
      <Projects projects={projects} categories={categories} onQuoteFor={quoteFor} />
      <Services services={services} onQuoteFor={() => scrollTo("contact")} />
      <Process />
      <Contact categories={categories} prefillType={prefillType} />
      <Footer />
    </div>
  );
}
